/** Thin fetch wrapper: same-origin cookies, CSRF header on writes, structured errors. */
let csrf: string | null = null;

export function setCsrf(token: string | null) {
  csrf = token;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
    readonly requestId?: string,
  ) {
    super(message);
  }
}

let csrfLoading: Promise<void> | null = null;
/** A write can happen before the session query resolved (e.g. deep links): fetch the CSRF token first. */
async function ensureCsrf() {
  if (csrf) return;
  csrfLoading ??= fetch('/api/me', { credentials: 'same-origin' })
    .then(async (r) => {
      if (r.ok) csrf = ((await r.json()) as { csrfToken?: string }).csrfToken ?? null;
    })
    .catch(() => undefined)
    .finally(() => (csrfLoading = null));
  await csrfLoading;
}

/**
 * Read a server-sent-event stream from a POST (the assistant answers a turn as it writes it, and
 * EventSource can only issue a GET). Yields each parsed `data:` payload.
 */
export async function postStream<T = any>(
  path: string,
  body: unknown,
  onEvent: (e: T) => void,
  opts: { signal?: AbortSignal; headers?: Record<string, string> } = {},
): Promise<void> {
  const { signal, headers: extra } = opts;
  await ensureCsrf();
  const res = await fetch(path, {
    method: 'POST',
    headers: { 'content-type': 'application/json', ...(csrf ? { 'x-csrf-token': csrf } : {}), ...extra },
    body: JSON.stringify(body),
    credentials: 'same-origin',
    signal,
  });
  if (!res.ok || !res.body) {
    const text = await res.text().catch(() => '');
    let json: any;
    try {
      json = text ? JSON.parse(text) : undefined;
    } catch {
      /* non-JSON */
    }
    throw new ApiError(res.status, json?.error?.code ?? 'error', json?.error?.message ?? `Request failed (${res.status})`, json?.error?.details, json?.requestId);
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    // Events are separated by a blank line; a partial one stays in the buffer until the rest arrives.
    let split: number;
    while ((split = buffer.indexOf('\n\n')) !== -1) {
      const frame = buffer.slice(0, split);
      buffer = buffer.slice(split + 2);
      for (const line of frame.split('\n')) {
        if (!line.startsWith('data:')) continue; // ': ping' and other comments
        try {
          onEvent(JSON.parse(line.slice(5).trim()) as T);
        } catch {
          /* a frame we cannot read is skipped rather than ending the stream */
        }
      }
    }
  }
}

export async function api<T = any>(method: string, path: string, body?: unknown): Promise<T> {
  if (method !== 'GET' && !path.startsWith('/api/auth/login') && !path.startsWith('/api/auth/signup')) await ensureCsrf();
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['content-type'] = 'application/json';
  if (method !== 'GET' && csrf) headers['x-csrf-token'] = csrf;
  const res = await fetch(path, { method, headers, body: body === undefined ? undefined : JSON.stringify(body), credentials: 'same-origin' });
  const text = await res.text();
  let json: any = undefined;
  try {
    json = text ? JSON.parse(text) : undefined;
  } catch {
    /* non-JSON */
  }
  if (!res.ok) {
    const e = json?.error;
    throw new ApiError(res.status, e?.code ?? 'error', e?.message ?? `Request failed (${res.status})`, e?.details, json?.requestId);
  }
  if (json && typeof json === 'object' && 'csrfToken' in json && json.csrfToken) csrf = json.csrfToken;
  return json as T;
}

export const get = <T = any>(p: string) => api<T>('GET', p);
export const post = <T = any>(p: string, b: unknown = {}) => api<T>('POST', p, b);
export const put = <T = any>(p: string, b: unknown = {}) => api<T>('PUT', p, b);
export const patch = <T = any>(p: string, b: unknown = {}) => api<T>('PATCH', p, b);
export const del = <T = any>(p: string) => api<T>('DELETE', p);

export function qs(params: Record<string, string | number | boolean | undefined | null>) {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') u.set(k, String(v));
  const s = u.toString();
  return s ? `?${s}` : '';
}
